<script setup>
import {getCurrentInstance, onMounted, reactive, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import {accessControlApi, articleListsApi, ga_Api} from "@/api/forwriting_index";
import LoadingIcon from "@/components/LoadingIcon.vue";
import CheckPointLimit from "@/views/forwriting/fw00_check_point_limit.vue"

const instance = getCurrentInstance()
const route = useRoute()
const router = useRouter();
const { t, locale } = useI18n();
const article_list_id = route.params.article_list_id

const article_list = reactive({
    "article_list_name": "",
})

// ==== GA Settings ====
const ga_settings = reactive({
  ga_property_id: "",
  ga_service_account_blob_path: "",
  ga_analysis_result: "",
  ga_match_prompt: "",
  ga_analyzed_at: "",
})
const ga_file = ref(null)
const ga_file_name = ref("")
const ga_save_msg = ref("")
const ga_save_success = ref(null)
const match_prompt_save_msg = ref("")
const match_prompt_save_success = ref(null)

// ==== GA Connection Test ====
const ga_test_result = reactive({
  message: "",
  success: null,
  sample_pages: [],
})


function isEmpty( val)
{
  return (
      val == null || (
          typeof val === "string" &&
          val.trim().length === 0
      )
  )
}

const check_modal_msg = ref( "")

function has_empty_fields()
{
  let t = instance.proxy.$t
  let f_list = ""

  if( isEmpty( article_list.article_list_name))
    f_list += t('ArticleLists_Update.list_name') + ", "

  // has some empty fields...
  if( f_list.length > 0) {
    check_modal_msg.value = f_list.substring( 0, f_list.length-2)
        + t( 'sales_templates_new.check_modal_msg')
    return true
  }

  return false
}

// 開啟對話框
const openCheckModal = () =>
{
  let has_empty = has_empty_fields()
  // console.log( "* has_empty", has_empty)

  if( has_empty)
  {
    // console.log( "** has_empty", has_empty)

    // 顯示警告對話框
    let btn = document.getElementById("showCheckModal");
    btn.click();
  }
  return has_empty
};

const handle_update = async ( ) => {
  if (openCheckModal()) {
    return
  }

  isLoading.show = true;

  const resp = await articleListsApi.update( article_list_id, article_list)
  if( resp.status !== 200) {
    isLoading.show = false;
    throw new Error("ERROR");
  }

  isLoading.show = false;
  await reload_data()
  // await router.push(`/forwriting/article_lists/${article_list_id}/articles`);
}

const handle_return = async ( ) => {
  await router.push(`/forwriting/article_lists/${article_list_id}/articles`);
}

// ==== GA Settings handlers ====

const handle_ga_file_change = (event) => {
  const file = event.target.files[0]
  if (file) {
    ga_file.value = file
    ga_file_name.value = file.name
  }
}

const getLocalizedMessage = (message, fallbackKey) => {
  if (!message) return t(fallbackKey)
  if (typeof message === "string" && message.includes("|")) {
    const [jaMsg, twMsg] = message.split("|")
    return locale.value === "tw" ? twMsg.trim() : jaMsg.trim()
  }
  return message
}

const handle_ga_save = async () => {
  isLoading.show = true
  ga_save_msg.value = ""
  ga_save_success.value = null

  try {
    const formData = new FormData()
    formData.append("ga_property_id", ga_settings.ga_property_id || "")

    if (ga_file.value) {
      formData.append("file", ga_file.value)
    }

    const resp = await ga_Api.update_ga_settings(article_list_id, formData)
    if (resp.status === 200) {
      ga_save_msg.value = t("setting.ga_save_success")
      ga_save_success.value = true
      await reload_data()
    }
  } catch (e) {
    console.error("GA save error:", e)
    ga_save_msg.value = getLocalizedMessage(e?.response?.data?.detail, "setting.ga_save_failed")
    ga_save_success.value = false
  } finally {
    isLoading.show = false
  }
}

const handle_match_prompt_save = async () => {
  isLoading.show = true
  match_prompt_save_msg.value = ""
  match_prompt_save_success.value = null
  try {
    const resp = await articleListsApi.update(article_list_id, { ga_match_prompt: ga_settings.ga_match_prompt })
    if (resp.status === 200) {
      match_prompt_save_msg.value = t("setting.ga_match_prompt_save_success")
      match_prompt_save_success.value = true
    }
  } catch (e) {
    console.error("match prompt save error:", e)
    match_prompt_save_msg.value = getLocalizedMessage(e?.response?.data?.detail, "setting.ga_match_prompt_save_failed")
    match_prompt_save_success.value = false
  } finally {
    isLoading.show = false
  }
}

const handle_ga_test = async () => {
  isLoading.show = true
  ga_test_result.message = ""
  ga_test_result.success = null
  ga_test_result.sample_pages = []

  try {
    const resp = await ga_Api.test_ga_connection(article_list_id)
    if (resp.data) {
      ga_test_result.success = resp.data.success
      ga_test_result.message = getLocalizedMessage(resp.data.message, "setting.ga_test_failed")
      ga_test_result.sample_pages = resp.data.sample_pages || []
    }
  } catch (e) {
    console.error("GA test error:", e)
    ga_test_result.success = false
    ga_test_result.message = getLocalizedMessage(e?.response?.data?.detail, "setting.ga_test_failed")
  } finally {
    isLoading.show = false
  }
}

const reload_data = async( ) => {
  const resp = await articleListsApi.get_1( article_list_id)
  article_list.article_list_name = resp.data.article_list_name

  // Load GA settings
  ga_settings.ga_property_id = resp.data.ga_property_id || ""
  ga_settings.ga_service_account_blob_path = resp.data.ga_service_account_blob_path || ""
  ga_settings.ga_analysis_result = resp.data.ga_analysis_result || ""
  ga_settings.ga_match_prompt = resp.data.ga_match_prompt || ""
  ga_settings.ga_analyzed_at = resp.data.ga_analyzed_at || ""
}

onMounted(async () => {
  if( ! await accessControlApi.check_option_access()) {
    await router.push(`/forwriting/options_error`)
    return
  }

  await reload_data()

  // check the point_limit
  point_limit.is_over = await ref_CheckPointLimit.value.check()
})

const isLoading = reactive({
  show: false
})

// ==== modal_over_points ====
const ref_CheckPointLimit = ref(null)
const point_limit = reactive({
  is_over: false,
})

</script>

<template>

  <!-- isLoading -->
  <LoadingIcon :is-loading="isLoading.show"></LoadingIcon>

  <!-- modal_over_points -->
  <CheckPointLimit ref="ref_CheckPointLimit"></CheckPointLimit>

  <!-- Check modal begin -->
  <button id="showCheckModal" data-bs-toggle="modal"
          data-bs-target="#CheckModal" style="display:none;"></button>
  <div class="modal fade" id="CheckModal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="exampleModalLabel">
            {{ $t('ArticleLists_Update.check_modal_title') }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p>{{check_modal_msg}}</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-primary" data-bs-dismiss="modal">
            {{ $t('ArticleLists_Update.check_modal_close') }}</button>
        </div>
      </div>
    </div>
  </div>
  <!-- Check modal end -->

  <div class="container">

    <!-- title-->
    <div class="row">
      <h4>{{ $t('ArticleLists_Update.h4') }}</h4>
    </div>

    <div class="row" style="margin-top: 30px">
      <label  class="form-label required">
        {{ $t('ArticleLists_Update.list_name') }}:</label>
      <input type="text" style="margin-left: 10px; width: 800px"
      v-model="article_list.article_list_name">
    </div>

    <!-- 2 buttons-->
    <div class="row" style="margin-top: 30px">
      <button class="btn btn-primary" style="width: 120px; margin-left: 20px"
      @click="handle_update">
        {{ $t('ArticleLists_Update.update') }}</button>

      <button class="btn btn-primary" style="width: 120px; margin-left: 20px"
      @click="handle_return">
        {{ $t('ArticleLists_Update.return') }}</button>
    </div>

    <!-- ==== GA Integration Settings ==== -->
    <div class="row" style="margin-top: 60px">
      <h5>Google Analytics {{ $t('setting.ga_integration') }}</h5>
      <hr style="width: 800px; margin-left: 10px">
    </div>

    <div class="row" style="margin-top: 10px; margin-bottom: 20px">
      <div style="margin-left: 10px; max-width: 800px">
        <small class="text-muted">{{ $t('setting.ga_overview') }}</small>
      </div>
    </div>

    <!-- GA Help Accordion -->
    <div class="row" style="margin-bottom: 20px">
      <div class="accordion" id="gaHelpAccordion" style="max-width: 800px; margin-left: 10px">

        <!-- Step 1: GA Property ID -->
        <div class="accordion-item">
          <h2 class="accordion-header" id="gaHelp1Head">
            <button class="accordion-button collapsed" type="button"
                    data-bs-toggle="collapse" data-bs-target="#gaHelp1"
                    aria-expanded="false" aria-controls="gaHelp1">
              {{ $t('setting.ga_help_step1_title') }}
            </button>
          </h2>
          <div id="gaHelp1" class="accordion-collapse collapse"
               aria-labelledby="gaHelp1Head" data-bs-parent="#gaHelpAccordion">
            <div class="accordion-body ga-help-body">
              <ol>
                <li v-html="$t('setting.ga_help_step1_1')"></li>
                <li v-html="$t('setting.ga_help_step1_2')"></li>
                <li v-html="$t('setting.ga_help_step1_3')"></li>
                <li v-html="$t('setting.ga_help_step1_4')"></li>
              </ol>
            </div>
          </div>
        </div>

        <!-- Step 2: Service Account -->
        <div class="accordion-item">
          <h2 class="accordion-header" id="gaHelp2Head">
            <button class="accordion-button collapsed" type="button"
                    data-bs-toggle="collapse" data-bs-target="#gaHelp2"
                    aria-expanded="false" aria-controls="gaHelp2">
              {{ $t('setting.ga_help_step2_title') }}
            </button>
          </h2>
          <div id="gaHelp2" class="accordion-collapse collapse"
               aria-labelledby="gaHelp2Head" data-bs-parent="#gaHelpAccordion">
            <div class="accordion-body ga-help-body">
              <ol>
                <li v-html="$t('setting.ga_help_step2_1')"></li>
                <li v-html="$t('setting.ga_help_step2_2')"></li>
                <li v-html="$t('setting.ga_help_step2_3')"></li>
                <li v-html="$t('setting.ga_help_step2_4')"></li>
                <li v-html="$t('setting.ga_help_step2_5')"></li>
              </ol>
            </div>
          </div>
        </div>

        <!-- Step 3: Grant GA Access -->
        <div class="accordion-item">
          <h2 class="accordion-header" id="gaHelp3Head">
            <button class="accordion-button collapsed" type="button"
                    data-bs-toggle="collapse" data-bs-target="#gaHelp3"
                    aria-expanded="false" aria-controls="gaHelp3">
              {{ $t('setting.ga_help_step3_title') }}
            </button>
          </h2>
          <div id="gaHelp3" class="accordion-collapse collapse"
               aria-labelledby="gaHelp3Head" data-bs-parent="#gaHelpAccordion">
            <div class="accordion-body ga-help-body">
              <ol>
                <li v-html="$t('setting.ga_help_step3_1')"></li>
                <li v-html="$t('setting.ga_help_step3_2')"></li>
                <li v-html="$t('setting.ga_help_step3_3')"></li>
                <li v-html="$t('setting.ga_help_step3_4')"></li>
              </ol>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- GA Form Fields -->
    <div class="row" style="margin-top: 15px">
      <label class="form-label">GA Property ID:</label>
      <input type="text" style="margin-left: 10px; width: 800px"
             placeholder="例: 123456789"
             v-model="ga_settings.ga_property_id">
      <small class="text-muted" style="margin-left: 10px">
        {{ $t('setting.ga_property_id_hint') }}
      </small>
    </div>

    <div class="row" style="margin-top: 20px">
      <label class="form-label">
        {{ $t('setting.ga_service_account_key') }}:
      </label>
      <div style="margin-left: 10px">
        <input type="file" accept=".json"
               @change="handle_ga_file_change"
               style="width: 800px">
        <div v-if="ga_settings.ga_service_account_blob_path" style="margin-top: 5px">
          <small class="text-success">
            {{ $t('setting.ga_key_uploaded') }}
          </small>
        </div>
        <small class="text-muted" style="display: block; margin-top: 5px">
          {{ $t('setting.ga_key_description') }}
        </small>
      </div>
    </div>

    <div class="row" style="margin-top: 20px">
      <button class="btn btn-primary" style="width: 200px; margin-left: 20px"
              @click="handle_ga_save">
        {{ $t('setting.ga_save') }}
      </button>

      <button class="btn btn-outline-secondary" style="width: 200px; margin-left: 15px"
              @click="handle_ga_test">
        {{ $t('setting.ga_test_connection') }}
      </button>

      <span v-if="ga_save_msg" style="margin-left: 15px; line-height: 38px"
            :class="ga_save_success ? 'text-success' : 'text-danger'">
        {{ ga_save_msg }}
      </span>
    </div>

    <!-- GA Connection Test Result -->
    <div v-if="ga_test_result.success !== null" class="row" style="margin-top: 15px; margin-left: 10px; max-width: 800px">
      <div class="alert" :class="ga_test_result.success ? 'alert-success' : 'alert-danger'">
        <strong>{{ ga_test_result.success ? '✅' : '❌' }}</strong>
        {{ ga_test_result.message }}
        <div v-if="ga_test_result.sample_pages && ga_test_result.sample_pages.length > 0" style="margin-top: 10px">
          <small class="text-muted">{{ $t('setting.ga_test_sample') }}:</small>
          <ul style="margin-top: 5px; margin-bottom: 0">
            <li v-for="(p, idx) in ga_test_result.sample_pages" :key="idx">
              <small>{{ p.title }} (PV: {{ p.views }})</small>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Match Prompt Edit -->
    <div class="row" style="margin-top: 40px">
      <h5>{{ $t('setting.ga_match_prompt_label') }}</h5>
      <hr style="width: 800px; margin-left: 10px">
    </div>
    <div class="row" style="margin-bottom: 10px">
      <div style="margin-left: 10px; max-width: 800px">
        <small class="text-muted">{{ $t('setting.ga_match_prompt_hint') }}</small>
      </div>
    </div>
    <div class="row">
      <textarea v-model="ga_settings.ga_match_prompt"
                style="margin-left: 10px; width: 800px; height: 180px"
                :placeholder="$t('setting.ga_match_prompt_placeholder')">
      </textarea>
    </div>
    <div class="row" style="margin-top: 10px">
      <button class="btn btn-primary" style="width: 200px; margin-left: 20px"
              @click="handle_match_prompt_save">
        {{ $t('setting.ga_match_prompt_save') }}
      </button>
      <span v-if="match_prompt_save_msg" style="margin-left: 15px; line-height: 38px"
            :class="match_prompt_save_success ? 'text-success' : 'text-danger'">
        {{ match_prompt_save_msg }}
      </span>
    </div>

  </div>
</template>

<style scoped>
.ga-help-body ol {
  padding-left: 20px;
  margin-bottom: 0;
}
.ga-help-body li {
  margin-bottom: 8px;
  line-height: 1.6;
}
</style>
